import type { OrderDetails } from '@/entities/order/model'
import type { StateDefinition } from '@/entities/state/model'
import { useAcknowledgeOrderMutation } from '@/entities/order/model'
import { useStateDictionaryQuery } from '@/entities/state/model'

/**
 * Determines if an order can be acknowledged, given the order and its state definition
 */
export function canAcknowledgeOrder(order: OrderDetails, state: StateDefinition): boolean {
  return Boolean(
    order
    && !order.isAcknowledged
    && order.status
    && state
    && state.canAcknowledge
    && state.isActive,
  )
}

/**
 * Hook to acknowledge an order if allowed.
 */
export function useAcknowledgeOrder() {
  const { mutateAsync, isPending } = useAcknowledgeOrderMutation()
  const { data: stateDictionary } = useStateDictionaryQuery()

  /**
   * @param order - The order to acknowledge
   * @returns {Promise<void | OrderDetails>} - Returns OrderDetails if acknowledgment is successful, otherwise resolves to undefined
   */
  async function updateOrderAcknowledgement(order: OrderDetails) {
    if (!stateDictionary) {
      console.error('State or state dictionary is not available for order acknowledgment. Order cannot be acknowledged.')
      return Promise.resolve()
    }

    if (!canAcknowledgeOrder(order, stateDictionary[order.status])) {
      console.warn('Order cannot be acknowledged due to state conditions.')
      return Promise.resolve()
    }

    return mutateAsync({
      orderId: order.orderId,
      lastUpdateTime: order.lastUpdateTime,
    })
  }

  return { updateOrderAcknowledgement, isPending }
}
